import type {
  AdminInquiry, AdminItem, AdminSale, InquiriesResponse, InquiryCreateInput, InquiryStatus, ItemCreateInput,
  ItemStatus, ItemUpdateInput, MeResponse, PlatformOverview, PlatformSale, PlatformSaleUpdateInput, PublicItemResponse,
  PublicSaleResponse, SaleCreateInput, SaleMemberInput, SaleUpdateInput, Seller, SellerCreatedResponse, SellerCreateInput,
  SellerRequestInput, SellerRequestStatus, SellerRequestView, SellerUpdateInput, SetupLink, SetupTokenInfo, UploadedImage,
} from '@sellit/shared';
import { APP_NAME, PRIVACY_VERSION, TERMS_VERSION } from '@sellit/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
  }
}

const networkError = () => new ApiError(0, 'network', `Couldn't reach ${APP_NAME}. Check your signal and try again.`);

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw networkError();
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // New Terms came out while the seller was signed in: reload so the acceptance screen shows.
    if (res.status === 403 && data?.error?.code === 'terms_required') window.location.reload();
    throw new ApiError(res.status, data?.error?.code ?? 'error', data?.error?.message ?? 'Something went wrong.', data?.error?.fields);
  }
  return data as T;
}

export const api = {
  me: () => request<MeResponse>('GET', '/api/auth/me'),
  login: (email: string, password: string) => request<MeResponse>('POST', '/api/auth/login', { email, password }),
  logout: () => request<void>('POST', '/api/auth/logout'),
  acceptTerms: () =>
    request<MeResponse>('POST', '/api/auth/accept-terms', { accepted: true, termsVersion: TERMS_VERSION, privacyVersion: PRIVACY_VERSION }),
  sale: (saleId: string) => request<AdminSale>('GET', `/api/admin/sales/${saleId}`),
  updateSale: (saleId: string, input: SaleUpdateInput) => request<AdminSale>('PATCH', `/api/admin/sales/${saleId}`, input),
  items: (saleId: string, status?: ItemStatus) =>
    request<{ items: AdminItem[] }>('GET', `/api/admin/sales/${saleId}/items${status ? `?status=${status}` : ''}`),
  createItem: (saleId: string, input: ItemCreateInput) => request<AdminItem>('POST', `/api/admin/sales/${saleId}/items`, input),
  item: (itemId: string) => request<AdminItem>('GET', `/api/admin/items/${itemId}`),
  updateItem: (itemId: string, input: ItemUpdateInput) => request<AdminItem>('PATCH', `/api/admin/items/${itemId}`, input),
  deleteItem: (itemId: string) => request<void>('DELETE', `/api/admin/items/${itemId}`),
  inquiries: (saleId: string, status?: InquiryStatus) =>
    request<InquiriesResponse>('GET', `/api/admin/sales/${saleId}/inquiries${status ? `?status=${status}` : ''}`),
  updateInquiry: (inquiryId: string, status: InquiryStatus) =>
    request<AdminInquiry>('PATCH', `/api/admin/inquiries/${inquiryId}`, { status }),
  sendInquiry: (slug: string, number: number, input: InquiryCreateInput) =>
    request<{ ok: true }>('POST', `/api/public/sales/${encodeURIComponent(slug)}/items/${number}/inquiries`, input),
  publicSale: (slug: string) => request<PublicSaleResponse>('GET', `/api/public/sales/${encodeURIComponent(slug)}`),
  publicItem: (slug: string, number: string) =>
    request<PublicItemResponse>('GET', `/api/public/sales/${encodeURIComponent(slug)}/items/${encodeURIComponent(number)}`),
  requestToSell: (input: SellerRequestInput) => request<{ ok: true }>('POST', '/api/public/seller-requests', input),
  checkSetupToken: (token: string) => request<SetupTokenInfo>('POST', '/api/auth/setup/check', { token }),
  setPassword: (token: string, password: string) => request<MeResponse>('POST', '/api/auth/setup', { token, password }),
};

/** Platform admin API (server-side checked: only platform admins get answers). */
export const platform = {
  overview: () => request<PlatformOverview>('GET', '/api/platform/overview'),
  sellers: () => request<{ sellers: Seller[] }>('GET', '/api/platform/sellers'),
  seller: (id: string) => request<Seller>('GET', `/api/platform/sellers/${id}`),
  createSeller: (input: SellerCreateInput) => request<SellerCreatedResponse>('POST', '/api/platform/sellers', input),
  updateSeller: (id: string, input: SellerUpdateInput) => request<Seller>('PATCH', `/api/platform/sellers/${id}`, input),
  setupLink: (id: string, sendEmail: boolean) => request<SetupLink>('POST', `/api/platform/sellers/${id}/setup-link`, { sendEmail }),
  sellerRequests: (status: SellerRequestStatus) =>
    request<{ requests: SellerRequestView[] }>('GET', `/api/platform/seller-requests?status=${status}`),
  sellerRequest: (id: string) => request<SellerRequestView>('GET', `/api/platform/seller-requests/${id}`),
  setSellerRequestStatus: (id: string, status: SellerRequestStatus) =>
    request<SellerRequestView>('PATCH', `/api/platform/seller-requests/${id}`, { status }),
  sales: () => request<{ sales: PlatformSale[] }>('GET', '/api/platform/sales'),
  sale: (id: string) => request<PlatformSale>('GET', `/api/platform/sales/${id}`),
  createSale: (input: SaleCreateInput) => request<PlatformSale>('POST', '/api/platform/sales', input),
  updateSale: (id: string, input: PlatformSaleUpdateInput) => request<PlatformSale>('PATCH', `/api/platform/sales/${id}`, input),
  setMembers: (id: string, members: SaleMemberInput[]) => request<PlatformSale>('PUT', `/api/platform/sales/${id}/members`, { members }),
  slugAvailable: (slug: string, except?: string) =>
    request<{ available: boolean; reason: string | null }>(
      'GET', `/api/platform/slug-available?slug=${encodeURIComponent(slug)}${except ? `&except=${except}` : ''}`,
    ),
};

/** Uploads one photo with progress reporting (fetch can't report upload progress, XHR can). */
export function uploadPhoto(saleId: string, photo: Blob, onProgress: (fraction: number) => void): Promise<UploadedImage> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/admin/sales/${saleId}/images`);
    xhr.withCredentials = true;
    xhr.timeout = 120_000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: any = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON error page */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as UploadedImage);
      else reject(new ApiError(xhr.status, data?.error?.code ?? 'error', data?.error?.message ?? 'Upload failed.'));
    };
    xhr.onerror = () => reject(networkError());
    xhr.ontimeout = () => reject(networkError());
    const form = new FormData();
    form.append('photo', photo, 'photo.jpg');
    xhr.send(form);
  });
}
